import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLanguage } from './LanguageContext';
import audioService from '../services/audioService';
import voiceoverService from '../services/voiceoverService';

/**
 * CaptionsContext
 *
 * Manages the on-demand read-along captions feature:
 *   - `enabled`     — user toggle, persisted in localStorage
 *   - `drawerOpen`  — full-transcript drawer visibility
 *   - Live narration tracking — segments come from
 *     `/config/navigationStopsGuide.json` for the active stop +
 *     language + variant ('connected' for guided tour,
 *     'standalone' for quick-nav single stops), and `currentTime`
 *     follows the real `<audio>` element exposed by
 *     `audioService.getNarrationAudio()`.
 */

const GUIDE_URL = '/config/navigationStopsGuide.json';

const CaptionsContext = createContext(null);

export const useCaptions = () => {
  const ctx = useContext(CaptionsContext);
  if (!ctx) throw new Error('useCaptions must be used inside <CaptionsProvider>');
  return ctx;
};

/**
 * Split narration text into sentence-sized segments.
 * Recognises Latin (. ! ?) and Devanagari (।) terminators, and also
 * the em-dash used for stylistic mid-sentence pauses in the scripts.
 */
function splitSentences(text) {
  if (!text) return [];
  // First normalise: collapse whitespace.
  const normalised = String(text).replace(/\s+/g, ' ').trim();
  if (!normalised) return [];
  // Match runs that end with a terminator OR the trailing chunk.
  const re = /[^.!?।]+[.!?।]+|[^.!?।]+$/g;
  const out = [];
  let m;
  while ((m = re.exec(normalised)) !== null) {
    const s = m[0].trim();
    if (s) out.push(s);
  }
  return out.length ? out : [normalised];
}

/**
 * Allocate segment timings proportionally across the audio duration
 * (character-count weighted). Returns segments with start/end/duration.
 */
function buildSegments(rawSegments, totalDuration) {
  if (!rawSegments || rawSegments.length === 0) {
    return [];
  }
  const dur = isFinite(totalDuration) && totalDuration > 0 ? totalDuration : 0;
  const totalChars = rawSegments.reduce((acc, s) => acc + Math.max(s.length, 1), 0);
  let cursor = 0;
  return rawSegments.map((text) => {
    const share = Math.max(text.length, 1) / totalChars;
    const duration = share * dur;
    const start = cursor;
    const end = cursor + duration;
    cursor = end;
    return { text, start, end, duration };
  });
}

/**
 * Pick the best matching text from a guide entry for the active
 * language + variant, with sensible fallbacks.
 */
function pickText(entry, language, variant) {
  if (!entry || !variant) return '';
  const candidates = [
    `${language}_${variant}`,
    `en_${variant}`,
    `mr_${variant}`,
    `hi_${variant}`,
  ];
  for (const k of candidates) {
    if (typeof entry[k] === 'string' && entry[k].trim()) return entry[k];
  }
  return '';
}

export const CaptionsProvider = ({ children }) => {
  const { language } = useLanguage();

  // Captions always start OFF on a fresh session. Users can opt in
  // per session via the inline tour-panel toggle (or the floating
  // FAB outside the tour); we intentionally do not persist the
  // choice so the next visit again opens with captions disabled.
  const [enabled, setEnabled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [guide, setGuide] = useState(null);
  const [narrationKey, setNarrationKey] = useState(null);
  const [narrationVariant, setNarrationVariant] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Persist enabled flag.
  useEffect(() => {
    // Intentionally not persisted — see initial state above.
  }, [enabled]);

  // Load the narration guide once at mount.
  useEffect(() => {
    let cancelled = false;
    fetch(GUIDE_URL, { cache: 'force-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j) setGuide(j); })
      .catch(() => { /* noop — captions will simply stay empty */ });
    return () => { cancelled = true; };
  }, []);

  // Poll audioService for narration state and live playhead. A rAF
  // loop keeps the active sentence highlight in lockstep with the
  // real <audio> element without us having to thread listeners
  // through the audioService's lazy element creation.
  const rafRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      let k = audioService.getCurrentNarrationKey?.() || null;
      let v = audioService.getCurrentNarrationVariant?.() || null;
      const a = audioService.getNarrationAudio?.() || null;

      // Fallback: POI voiceover path. When a POI's voiceoverTagId is
      // a tour-stop key (e.g. "stop_3_garbhagriha"), playback is routed
      // through the Web Audio buffer path (audioService.playVoiceover)
      // rather than the <audio> narration element — so the regular
      // narration getters return null. Surface it here so the read-along
      // strip still appears for POI-triggered tour-stop narrations.
      if (!k && voiceoverService?.getCurrentTagId) {
        const tagId = voiceoverService.getCurrentTagId();
        if (tagId && /^stop_\d+_/.test(tagId)) {
          k = tagId;
          v = 'standalone';
        }
      }

      setNarrationKey((prev) => (prev !== k ? k : prev));
      setNarrationVariant((prev) => (prev !== v ? v : prev));

      if (a) {
        const dur = isFinite(a.duration) ? a.duration : 0;
        setTotalDuration((prev) => (prev !== dur ? dur : prev));
        setCurrentTime(a.currentTime || 0);
        setPlaying(!a.paused);
      } else if (k && audioService.isVoiceoverPlaying?.()) {
        // Drive captions from the Web Audio voiceover playhead.
        const dur = audioService.getVoiceoverDuration?.() || 0;
        const cur = audioService.getVoiceoverElapsed?.() || 0;
        setTotalDuration((prev) => (prev !== dur ? dur : prev));
        setCurrentTime(cur);
        setPlaying(true);
      } else {
        setCurrentTime(0);
        setTotalDuration(0);
        setPlaying(false);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Raw sentence list for the active stop.
  const rawSegments = useMemo(() => {
    if (!guide || !narrationKey || !narrationVariant) return [];
    const text = pickText(guide[narrationKey], language, narrationVariant);
    return splitSentences(text);
  }, [guide, narrationKey, narrationVariant, language]);

  // Timed segments. Recomputed when raw text changes OR when the
  // audio duration becomes known (loadedmetadata fires after src=).
  const segments = useMemo(
    () => buildSegments(rawSegments, totalDuration),
    [rawSegments, totalDuration]
  );

  // Currently active segment index.
  const currentSegmentIndex = useMemo(() => {
    if (!segments.length) return -1;
    let idx = -1;
    for (let i = 0; i < segments.length; i += 1) {
      if (segments[i].start <= currentTime) idx = i;
      else break;
    }
    return idx;
  }, [segments, currentTime]);

  // Playback controls — proxy to the real audio element.
  const play = useCallback(() => {
    const a = audioService.getNarrationAudio?.();
    if (a) { try { a.play().catch(() => {}); } catch { /* noop */ } }
  }, []);
  const pause = useCallback(() => {
    const a = audioService.getNarrationAudio?.();
    if (a) { try { a.pause(); } catch { /* noop */ } }
  }, []);
  const seek = useCallback((t) => {
    const a = audioService.getNarrationAudio?.();
    if (a) {
      const dur = isFinite(a.duration) ? a.duration : t;
      try { a.currentTime = Math.max(0, Math.min(t, dur)); } catch { /* noop */ }
    }
  }, []);
  const restart = useCallback(() => {
    const a = audioService.getNarrationAudio?.();
    if (a) {
      try { a.currentTime = 0; a.play().catch(() => {}); } catch { /* noop */ }
    }
  }, []);

  const toggleEnabled = useCallback(() => {
    setEnabled((e) => {
      const next = !e;
      if (!next) setDrawerOpen(false);
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    enabled,
    toggleEnabled,
    drawerOpen,
    setDrawerOpen,
    playing,
    play,
    pause,
    seek,
    restart,
    currentTime,
    totalDuration,
    segments,
    currentSegmentIndex,
    narrationKey,
  }), [
    enabled, toggleEnabled, drawerOpen, playing, play, pause, seek, restart,
    currentTime, totalDuration, segments, currentSegmentIndex, narrationKey,
  ]);

  return (
    <CaptionsContext.Provider value={value}>
      {children}
    </CaptionsContext.Provider>
  );
};

export default CaptionsContext;
